import { BookOpen, X } from "lucide-react";
import { DispatchBadge } from "@/features/power_projects/components/ui";
import type { DispatchStatus } from "@/features/power_projects/types";

// One lifecycle step: status badge + human explanation of what it means and who acts.
function Step({ status, children }: { status: DispatchStatus; children: React.ReactNode }) {
  return (
    <li className="relative pl-4 pb-3 last:pb-0 border-l border-[var(--color-border)] last:border-l-transparent ml-1.5">
      <span className="absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full bg-[var(--color-bg-surface)] border-2 border-[var(--color-primary)]" />
      <div className="mb-0.5"><DispatchBadge status={status} /></div>
      <div className="text-[11.5px] leading-relaxed text-[var(--color-text-secondary)]">{children}</div>
    </li>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="section-label text-[var(--color-text-primary)]">{title}</h4>
      {children}
    </section>
  );
}

// ── Vademecum projektanta — cykl życia dokumentacji w DLC_02, opisany po ludzku ──
export function VademecumPanel({ onClose }: { onClose: () => void }) {
  return (
    <div className="card p-3.5 space-y-4 text-[12px]">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <BookOpen size={14} className="text-[var(--color-primary)]" />
          <div>
            <h3 className="section-label text-[var(--color-text-primary)]">Vademecum projektanta</h3>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Cykl życia dokumentacji · DLC_02
            </div>
          </div>
        </div>
        <button
          type="button"
          title="Zamknij vademecum"
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          onClick={onClose}
        >
          <X size={14} />
        </button>
      </div>

      <Section title="Pisma przychodzące (wpływ)">
        <p className="text-[11.5px] leading-relaxed text-[var(--color-text-secondary)]">
          Każde pismo, które wpływa do firmy (papier, e-Doręczenia, e-mail), sekretariat rejestruje
          w dzienniku ze skanem. Dalej pismo przechodzi trzy kroki:
        </p>
        <ol className="mt-2">
          <Step status="received">
            Pismo jest w dzienniku, ale nikt go jeszcze formalnie nie dostał. Na tym etapie obieg
            dokumentów może jeszcze <strong>poprawić wpis</strong> albo <strong>podzielić go na
            pozycje</strong> (patrz niżej).
          </Step>
          <Step status="filed">
            Dekretacja — pismo zostało skierowane do konkretnych osób i/lub przedsięwzięcia.
            Adresaci dostają powiadomienie i widzą je w zakładce <strong>Moja korespondencja</strong>.
          </Step>
          <Step status="acknowledged">
            Adresat kliknął „Oznacz jako odebrane" — potwierdził, że zapoznał się z pismem.
            Wystarczy potwierdzenie jednej z zadekretowanych osób.
          </Step>
        </ol>
      </Section>

      <Section title="Dodanie adresata po dekretacji">
        <p className="text-[11.5px] leading-relaxed text-[var(--color-text-secondary)]">
          Zadekretowane pismo nie ma zamkniętej listy adresatów. W kolumnie „Status / cel" akcja{" "}
          <strong>„+ dodaj adresata"</strong> dokłada kolejne osoby — każda dostaje powiadomienie i
          widzi pismo u siebie jako <strong>Zadekretowane</strong>, więc może potwierdzić własny
          odbiór. Adresata dodaje <strong>obecny adresat albo dyrektor</strong> (dział obiegu
          dekretuje przy rejestracji i tu już nie dokłada osób); jeśli
          odbiór był już potwierdzony, wpis wraca do statusu „Zadekretowane" — <em>tylko</em> dla
          nowej osoby, bo przy nazwiskach widać, kto już potwierdził (✓). Adresata może{" "}
          <strong>usunąć</strong> wyłącznie dyrektor / dział obiegu, i nigdy ostatniego —
          wpis musi zostać u kogoś.
        </p>
      </Section>

      <Section title="Pisma wychodzące — papier">
        <p className="text-[11.5px] leading-relaxed text-[var(--color-text-secondary)]">
          Papierowe wysyłki (np. wnioski z DLC_01) trafiają do kolejki nadawczej i są obsługiwane
          przez dział obiegu dokumentów:
        </p>
        <ol className="mt-2">
          <Step status="queued">Pismo czeka w kolejce na przygotowanie do wysyłki.</Step>
          <Step status="prepared">Zaadresowane i spakowane — gotowe do nadania.</Step>
          <Step status="sent">Nadane — tu zwykle pojawia się numer nadania (śledzenie).</Step>
          <Step status="delivered">
            Adresat odebrał przesyłkę. Często razem ze <strong>zwrotką / UPO</strong> podpiętą do wpisu.
          </Step>
        </ol>
        <div className="mt-1.5 space-y-1 text-[11.5px] leading-relaxed text-[var(--color-text-secondary)]">
          <div className="flex flex-wrap items-center gap-1.5">
            <DispatchBadge status="returned" />
            <span>przesyłka wróciła — można ją nadać ponownie.</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <DispatchBadge status="undeliverable" />
            <span>nie da się doręczyć (zły adres itp.) — wymaga decyzji, co dalej.</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <DispatchBadge status="cancelled" />
            <span>
              autor wycofał pismo z kolejki (zakładka <strong>„Moje pisma do wysłania"</strong> przy
              zleceniach wydruku) — nie wysyłamy go. Możliwe tylko przed nadaniem; wpis zostaje w
              dzienniku wraz z powodem.
            </span>
          </div>
        </div>
      </Section>

      <Section title="Pozostałe kanały (e-Doręczenia / e-mail / inne)">
        <p className="text-[11.5px] leading-relaxed text-[var(--color-text-secondary)]">
          Wysyłka elektroniczna nie ma kolejki fizycznego nadania — wpis od razu dostaje status{" "}
          <strong>Wysłane</strong> i służy jako ślad w rejestrze.
        </p>
      </Section>

      <Section title="Podział wpływu na pozycje">
        <p className="text-[11.5px] leading-relaxed text-[var(--color-text-secondary)]">
          Jedna przesyłka często zawiera dokumenty kilku spraw (np. dwa różne przedsięwzięcia w
          jednej kopercie). Do czasu potwierdzenia odbioru akcja <strong>„Podziel"</strong>{" "}
          rozbija je na dwie lub więcej pozycji — dla każdej wskazujesz pliki, osoby i
          przedsięwzięcie. Każda pozycja dziedziczy dane przesyłki (nadawcę, datę, nr) i dalej
          żyje własnym cyklem. Żaden załącznik nie może zostać pominięty, a ten sam plik może
          trafić do kilku pozycji.
        </p>
      </Section>

      <Section title="Kto co robi">
        <ul className="space-y-1 text-[11.5px] leading-relaxed text-[var(--color-text-secondary)] list-disc pl-4">
          <li>
            <strong>Obieg dokumentów</strong> (uprawnienie „Firmowy obieg dokumentów") — rejestruje
            wpływy, dekretuje, dzieli na pozycje i prowadzi kolejkę wysyłek.
          </li>
          <li>
            <strong>Projektant / adresat</strong> — dostaje powiadomienie o dekretacji, czyta pismo
            w „Moja korespondencja" i potwierdza odbiór. Pismo skierowane <em>tylko do niego</em>,
            które czeka jeszcze na dekretację, dekretuje sam na siebie — bez uprawnienia do
            firmowego obiegu. Może też pobrać zaznaczone załączniki jednym ZIP-em.
          </li>
          <li>
            <strong>Koordynator przedsięwzięcia</strong> — pismo zadekretowane do niego może z
            „Mojej korespondencji" podpiąć pod jedno ze <em>swoich</em> przedsięwzięć (kolumna
            „Projekt" → „podepnij projekt").
          </li>
        </ul>
      </Section>
    </div>
  );
}
